import React, { useState } from 'react';
import { Reservation } from '../../types';
import Modal from '../shared/Modal';
import { resendBookingConfirmation } from '../../services/reservationService';

interface ResendAgreementModalProps {
  reservation: Reservation;
  onClose: () => void;
  onSuccess: () => void;
}

const ResendAgreementModal: React.FC<ResendAgreementModalProps> = ({
  reservation,
  onClose,
  onSuccess,
}) => {
  const [overrideEmail, setOverrideEmail] = useState('');
  const [includePdf, setIncludePdf] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      await resendBookingConfirmation(
        reservation.confirmationCode,
        overrideEmail.trim() || undefined,
        includePdf
      );
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to resend booking confirmation');
    } finally {
      setSending(false);
    }
  };

  const footer = success ? (
    <button className="btn btn-secondary" onClick={() => { onSuccess(); onClose(); }}>
      CLOSE
    </button>
  ) : (
    <>
      <button className="btn btn-secondary" onClick={onClose} disabled={sending}>
        CANCEL
      </button>
      <button className="btn btn-primary" onClick={handleSend} disabled={sending}>
        {sending ? 'SENDING...' : 'SEND CONFIRMATION'}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Reissue Confirmation & Agreement"
      size="small"
      footer={footer}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
          Resend the booking confirmation email (with camping agreement PDF) for reservation{' '}
          <strong>{reservation.confirmationCode}</strong>.
        </p>

        <div>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '0.9rem' }}>
            Guest Email
          </label>
          <span style={{ fontSize: '0.95rem' }}>{reservation.guestEmail || '(none on file)'}</span>
        </div>

        <div>
          <label
            htmlFor="override-email"
            style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '0.9rem' }}
          >
            Override Email (optional)
          </label>
          <input
            id="override-email"
            type="email"
            value={overrideEmail}
            onChange={(e) => setOverrideEmail(e.target.value)}
            placeholder="Send to a different address"
            disabled={sending || success}
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid var(--border-color, #ccc)',
              borderRadius: '4px',
              fontSize: '0.95rem',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div>
          <label
            style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: sending || success ? 'default' : 'pointer' }}
          >
            <input
              type="checkbox"
              checked={includePdf}
              onChange={(e) => setIncludePdf(e.target.checked)}
              disabled={sending || success}
            />
            Include camping agreement PDF
          </label>
        </div>

        {error && (
          <p style={{ margin: 0, color: '#d32f2f', fontSize: '0.9rem' }}>{error}</p>
        )}

        {success && (
          <p style={{ margin: 0, color: '#2e7d32', fontSize: '0.9rem' }}>
            Booking confirmation & agreement sent successfully.
          </p>
        )}
      </div>
    </Modal>
  );
};

export default ResendAgreementModal;
